import AppApi from './AppApi';
import { apiRootUrl } from '../config.local';

export interface Integration {
	integrationId: number;
	name: string;
}
export interface UserIntegration {
	integrationId: number;
	customerId: number;
	name: string;
	id: number;
	status: string;
	accessToken: string;
	refreshToken: string;
	integrationUserID: string;
	configuration: string;
}

export interface Configuration {
	id: string;
	certifiationId: string;
	code: string;
}

export interface OAuthTokenResponse {
	accessToken: string;
	refreshToken?: string;
}

const appApi = new AppApi(apiRootUrl);

export const getAllIntegrations = async (): Promise<Integration[]> => {
	return await appApi.ajax('get', `/api/integrations`, null);
};

export const getIntegrationsByCustomerId = async (
	customerId: number,
	integrationUserId?: number
): Promise<UserIntegration[]> => {
	const queryString = integrationUserId ? `?integrationUserId=${integrationUserId}` : '';
	return await appApi.ajax('get', `/api/integrations/${customerId}${queryString}`);
};

export const getOAuthUrl = async (
	customerId: number,
	integrationId: number,
	redirectUri: string,
	state?: string
): Promise<{ url: string }> => {
	const params = new URLSearchParams({ integrationId: String(integrationId), redirectUri });
	if (state) params.append('state', state);
	return await appApi.ajax('get', `/api/integrations/${customerId}/oauth-url?${params.toString()}`);
};

export const exchangeAuthCode = async (
	customerId: number,
	integrationId: number,
	authCode: string,
	redirectUri: string,
	integrationUserId: number
): Promise<OAuthTokenResponse> => {
	return await appApi.ajax('post', `/api/integrations/${customerId}/exchange-code`, {
		integrationId,
		authCode,
		redirectUri,
		integrationUserId
	});
};

export const authorizeSmartRecruiters = async (
	customerId: number,
	integrationId: number
): Promise<OAuthTokenResponse> => {
	return await appApi.ajax(
		'post',
		`/api/integrations/${customerId}/smartrecruiters/authorize?integrationId=${integrationId}`,
		{}
	);
};

export const smartRecruitersConsent = async (
	customerId: number,
	integrationId: number,
	companyId: string
): Promise<void> => {
	return await appApi.ajax(
		'post',
		`/api/integrations/${customerId}/smartrecruiters/consent?integrationId=${integrationId}&companyId=${companyId}`,
		{}
	);
};

export const refreshIntegrationToken = async (
	customerId: number,
	integrationId: number,
	integrationUserId: number
): Promise<OAuthTokenResponse> => {
	return await appApi.ajax('post', `/api/integrations/${customerId}/refresh-token`, {
		integrationId,
		integrationUserId
	});
};

export const addIntegrationToCustomerUnboundUser = async (
	customerId: number,
	integrationId: string,
	accessToken: string,
	refreshToken: string,
	integrationUserId?: number
) => {
	return await appApi.ajax('post', `/api/integrations`, {
		customerId,
		integrationId,
		status: 'ACTIVE',
		accessToken,
		refreshToken,
		integrationUserID: integrationUserId
	});
};

export const createCustomerSubscription = async (customerId: number, triggerName: string) => {
	return await appApi.ajax('post', `/api/integrations/subscription`, {
		customerId,
		triggerName,
		URL: '/api/integration/smartrecruiters/status-update'
	});
};

export const deleteCustomerSubscription = async (customerId: number, triggerName: string) => {
	return await appApi.ajax('delete', `/api/integrations/${customerId}/subscription/${triggerName}`);
};

export const getCustomerSubscriptions = async (customerId: number, triggerName: string) => {
	return await appApi.ajax('delete', `/api/integrations/${customerId}/subscription/${triggerName}`);
};

export const updateIntegrationToCustomer = async (
	customerId: number,
	integrationId: number,
	accessToken: string,
	refreshToken: string,
	integrationUserID: number,
	configuration: string
) => {
	return await appApi.ajax('put', `/api/integrations/${customerId}?integrationId=${integrationId}`, {
		customerId,
		integrationId,
		status: 'ACTIVE',
		accessToken,
		refreshToken,
		integrationUserID,
		configuration
	});
};

export const deleteIntegrationFromCustomer = async (
	customerId: number,
	integrationId: string,
	integrationUserId?: number
) => {
	const integrationUserIdParam = integrationUserId ? `&integrationUserId=${integrationUserId}` : '';
	return await appApi.ajax(
		'delete',
		`/api/integrations/${customerId}?customerId=${customerId}&integrationId=${integrationId}${integrationUserIdParam}`
	);
};
